// auth/hooks/useLogout.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/store/hooks";
import { authApi } from "../api/auth.api";
import { clearAuthState } from "../utils/auth.utils";
import type { AuthCallbacks } from "../types/auth.types";
import { logger } from "@/core";
import { toastService } from "@/core/services/toast.service";

/**
 * useLogout — Handles user logout
 *
 * Flow:
 * 1. Call logout API (clears httpOnly cookie on server)
 * 2. Clear Redux state
 * 3. Clear React Query cache
 * 4. Redirect to login
 */
export const useLogout = (callbacks?: AuthCallbacks) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: () => authApi.logout(),

    onSuccess: () => {
      toastService.success("Logout", "logout successfully");
      clearAuthState(dispatch, queryClient);
      navigate("/login", { replace: true });
      logger.info("Logout successful");
      callbacks?.onSuccess?.();
    },

    onError: (error) => {
      toastService.error("Logout Failed", error.message);
      // Even on error, clear local state (server session might be expired)
      clearAuthState(dispatch, queryClient);
      navigate("login", { replace: true });
      logger.error("Logout API failed, cleared local state anyway", error);
      callbacks?.onError?.(error);
    },
  });
};
